import { Pair, Trade as V2Trade } from 'elephantswapv2-sdk'
import { Currency, CurrencyAmount, TradeType } from 'elephantswapv3-sdk-core'
import { useMemo } from 'react'
import { ClassicTrade, InterfaceTrade, TradeState } from 'state/routing/types'

import { useAllCurrencyCombinations } from './useAllCurrencyCombinations'
import { PairState, useV2Pairs } from './useV2Pairs'

/**
 * Quotes V2 directly from on-chain reserves. This is intentionally independent
 * of the smart-order-router cache so small swaps do not need a larger quote to
 * warm the router before a valid route is found.
 */
export function useClientSideV2Trade(
  tradeType: TradeType,
  amountSpecified?: CurrencyAmount<Currency>,
  otherCurrency?: Currency
): { state: TradeState; trade?: InterfaceTrade } {
  const [currencyIn, currencyOut] =
    tradeType === TradeType.EXACT_INPUT
      ? [amountSpecified?.currency, otherCurrency]
      : [otherCurrency, amountSpecified?.currency]
  const currencyCombinations = useAllCurrencyCombinations(currencyIn, currencyOut)
  const pairResults = useV2Pairs(currencyCombinations)

  return useMemo(() => {
    if (!amountSpecified || !currencyIn || !currencyOut || currencyIn.wrapped.equals(currencyOut.wrapped)) {
      return { state: TradeState.INVALID, trade: undefined }
    }

    const pairs = pairResults
      .filter((result): result is [PairState.EXISTS, Pair] => result[0] === PairState.EXISTS && result[1] !== null)
      .map(([, pair]) => pair)

    if (pairs.length === 0) {
      return {
        state: pairResults.some(([state]) => state === PairState.LOADING)
          ? TradeState.LOADING
          : TradeState.NO_ROUTE_FOUND,
        trade: undefined,
      }
    }

    try {
      const v2Trade =
        tradeType === TradeType.EXACT_INPUT
          ? V2Trade.bestTradeExactIn(pairs, amountSpecified.wrapped, currencyOut.wrapped, {
              maxHops: 2,
              maxNumResults: 1,
            })[0]
          : V2Trade.bestTradeExactOut(pairs, currencyIn.wrapped, amountSpecified.wrapped, {
              maxHops: 2,
              maxNumResults: 1,
            })[0]

      if (!v2Trade) return { state: TradeState.NO_ROUTE_FOUND, trade: undefined }

      return {
        state: TradeState.VALID,
        trade: new ClassicTrade({
          v2Routes: [
            {
              routev2: v2Trade.route,
              inputAmount: CurrencyAmount.fromFractionalAmount(
                currencyIn,
                v2Trade.inputAmount.numerator,
                v2Trade.inputAmount.denominator
              ),
              outputAmount: CurrencyAmount.fromFractionalAmount(
                currencyOut,
                v2Trade.outputAmount.numerator,
                v2Trade.outputAmount.denominator
              ),
            },
          ],
          v3Routes: [],
          tradeType,
        }),
      }
    } catch {
      // The SDK rejects true dust amounts whose output rounds to zero.
      return { state: TradeState.NO_ROUTE_FOUND, trade: undefined }
    }
  }, [amountSpecified, currencyIn, currencyOut, pairResults, tradeType])
}
